/**
 * Hero, from hung-hola blocks/hero/view.js: the dot-wave background and the particle logo mark, both WebGL2.
 * The mark is copied as is from the owner's animation source (apps/2026-10-particle-logo in site-of-the-month-repo,
 * components/ParticleMark): keep it identical, tune there first. The dot wave follows the owner's own brief and is
 * tuned with ?tune (a slider panel, Copy puts WAVE and INTRO on the clipboard to paste below).
 * Intro on load (hung-hola docs/animation/hero.md): grid → hand → badge and buttons. Those are the shared fade-in
 * (assets/js/fade-in.ts), waiting for the "px:hero-intro" event sent here (data-px-fade-in-after="hero-intro"); on
 * landscape desktop the whole text joins it in turn; at ≤1280 or on a portrait screen it shows from first paint (render.php).
 * Without WebGL2 both canvases stay empty: the brand background and the SVG mark still show.
 * ?still / reduced motion: no intro, the wave drawn once on its first frame, the mark at rest; nothing follows the
 * pointer. Without a mouse (phones, tablets: owner 2026-10-21) the intro plays but nothing follows the pointer and a
 * tap drops no rings. Starts after first paint so the h1 (LCP) never waits for shader compiles; pauses off screen.
 * The mark's CPU simulation rests once its dots have settled and the pointer is still (PageSpeed, 2026-10-21).
 * Devices that can't keep up get the still hero, the same for every visitor (plan 005, owner 2026-10-21: the motion
 * stays wherever it runs): software WebGL (no GPU: PageSpeed's servers, VMs, hardware acceleration off; ~200 ms of
 * CPU a frame) from the start, and a device missing SLOW.budget after the intro from that frame on.
 */
import { isStill, softwareWebGL } from '../../assets/js/motion';
import { DOTS, createProgram, dotStir, dotsPlay } from '../../assets/js/dot-ripple';

type Wave = { resize: () => void; draw: (seconds: number, intro: number, dt: number) => void };
type Mark = { resize: (cssSize: number) => void; draw: (maskMix: number) => void };

/* ------------------------------------------------------------------
 * 1. Dot wave (owner's brief + screen recording of the Figma prototype, 2026-10-18):
 * a finite sheet of rows x columns of dots laid on a plane, seen in perspective from above.
 * Near dots are bigger and sparser, far dots smaller, denser and fading out toward the horizon.
 * Both sides ride high, the middle sags, so the near edge shows as a wavy line in the lower
 * part of the hero with empty background under it. The sheet ripples all the time like cloth.
 * The pointer plays with the sheet itself (owner 2026-10-21: flat circles and rings on the screen ignored the
 * perspective, and the push out to a circle's rim drilled a hole), at the sheet's point under the pointer: the cloth
 * swells gently under the mouse, its dots a touch brighter (SHEET_HOVER), and a click sends one soft ripple across it,
 * lifting and lowering the dots on the way (SHEET_RIPPLE). The pointer itself is dot-ripple.ts's (dotStir()).
 * Computed on the GPU per dot (~50k).
 * Drawn in grey like the video; the canvas carries the layer's opacity 80% + Hard Light (render.php).
 * The video was 16:9 with scale mode FILL: the drawing keeps a 16:9 frame cropped to the hero like object-fit: cover,
 * but the canvas is only the hero's size (the viewport reaches past it), so no pixel off screen is drawn: a phone
 * held upright drew ~4x the pixels it shows.
 * Portrait screens (owner 2026-10-21): the dots get their own 16:9 frame, centred on the band between the header and
 * the text's frosted strip (hero.css) and PORTRAIT.scale times its height, so they show above the text instead of
 * behind it; dot size follows that frame, opacity PORTRAIT.alpha; the grey ground under them keeps the hero-wide frame.
 * Light on phones and tablets (no mouse; owner 2026-10-21: "on phone things could be reduced"): half the dots
 * (LITE.dots, same sheet, sparser), 1.5x pixel ratio at most (2x elsewhere), 30 fps after the intro.
 * ------------------------------------------------------------------ */
const LITE = { dots: 0.7, dpr: 1.5, fps: 30 }; // dots: share of columns and of rows (0.7 x 0.7 = half the dots)
// portrait: the dots' frame is this many times the band's height, centred on it (owner 2026-10-21: fitted to the band
// the dots were too dense, noise over the logo; 2.2 picked from 1.6-3 on 375 and 390 screenshots)
const PORTRAIT = { scale: 2.2, alpha: 0.6 }; // alpha: times the dots' opacity (owner: more transparent there)
// the mouse's swell on the sheet, world units (the sheet is ~9 x 4.8)
const SHEET_HOVER = {
	radius: 0.35, // the mound's softness (where it is down to ~37%)
	height: 0.1, // how far the cloth rises under the pointer
	light: 0.6, // extra opacity of the dots on it
	grow: 0.25, // extra size of the dots on it
};
// a click's ripple on the sheet, world units
const SHEET_RIPPLE = {
	speed: 1.4, // per second the ring runs out
	width: 0.5, // the ring: one rise and one fall
	height: 0.07, // how far a dot rises and falls
	life: 2.4, // s until it has died out
	pick: 40, // px: a pointer farther than this from every dot (above the sheet) is off it: no swell, no ripple
};

// a band of the canvas, client px from its top
type Band = { top: number; bottom: number };
type View = { x: number; y: number; w: number; h: number }; // device px, y from the bottom (gl.viewport)
const WAVE = {
	cols: 320, // dots across
	rows: 170, // dots into depth
	gap: 0.028, // spacing between dots, world units (sheet size = count x gap)
	near: 1.6, // distance from the camera to the near edge
	dot: 3.4, // dot size px at the reference distance
	alphaNear: 1, // dot opacity at the near edge
	alphaFar: 0.35, // dot opacity before the far fade
	fadeFar: 0.35, // last part of the depth that fades out to 0
	fadeSides: 0.04, // part of the width that fades out at each side
	sides: 1.4, // how high both sides ride
	sidesCurve: 1.4, // 1 = straight slope, higher = flat middle, steep sides
	dip: 0.8, // how deep the middle sags (owner 2026-10-20: down to just below the lowest finger of the hand)
	dipNear: 0.56, // share of `dip` at the near edge (the near edge keeps its old depth, only the far part sinks)
	dipWidth: 1.4, // width of the sag, world units
	amp: 0.3, // ripple height
	freq: 2.4, // ripple scale
	speed: 0.35, // ripple speed
	tilt: 0.38, // camera pitch, radians
	camY: 1.2, // camera height
};
const WAVE_DEFAULTS = { ...WAVE };

/*
 * Intro on page load (owner, 2026-10-19): the grid sweeps in from the near edge to the horizon while it bends from
 * flat into its shape, then the mark's particles fly in and settle into the hand, then the hero text fades in.
 */
const INTRO = {
	grid: 1.5, // s, grid sweep
	gridSoft: 0.25, // depth of the sweep front, part of the sheet
	gridCrest: 0.12, // how high a row rises as the front passes it, world units
	gap: -0.6, // s from the end of the grid sweep to the hand (negative: the hand starts while the grid sweeps)
	hand: 1, // s, hand particles flying in
	handSpread: 0.5, // part of the hand time that staggers the particles
	handScatter: 60, // how far each particle starts from home, box units (small: a blurred hand, not a cloud)
};
const INTRO_DEFAULTS = { ...INTRO };
const easeOut = (p: number, power: number): number => 1 - Math.pow(1 - Math.min(1, Math.max(0, p)), power);

const BG_VERT = `#version 300 es
in vec2 aPos;
out vec2 vUv;
void main() { vUv = aPos * 0.5 + 0.5; gl_Position = vec4(aPos, 0.0, 1.0); }`;

// grey ground sampled from the video frame: ~141 top-left, ~147 top, ~211 at the top right and both mid sides,
// ~132 bottom centre, ~156 centre
const BG_FRAG = `#version 300 es
precision highp float;
in vec2 vUv;
out vec4 outColor;
float blob(vec2 uv, vec2 c, vec2 r) { vec2 d = (uv - c) / r; return exp(-dot(d, d)); }
void main() {
  vec2 uv = vec2(vUv.x, 1.0 - vUv.y);
  float g = 0.55;
  g += 0.30 * blob(uv, vec2(0.0, 0.55), vec2(0.35, 0.45));
  g += 0.30 * blob(uv, vec2(1.0, 0.35), vec2(0.4, 0.55));
  g += 0.18 * blob(uv, vec2(0.0, 1.0), vec2(0.3, 0.3));
  g -= 0.05 * blob(uv, vec2(0.5, 1.0), vec2(0.25, 0.2));
  outColor = vec4(vec3(clamp(g, 0.0, 1.0)), 1.0);
}`;

const DOT_VERT = `#version 300 es
in vec2 aGrid;          // 0..1 across, 0..1 into depth
uniform float uTime;
uniform float uAspect;
uniform vec2 uSize;     // sheet width, depth (world units)
uniform float uNear;
uniform vec3 uShape;    // sides, sides curve, dip
uniform float uDipWidth;
uniform float uDipNear;  // share of the sag at the near edge, rising to 1 at the far edge
uniform vec2 uRipple;   // amp, freq
uniform vec2 uCam;      // pitch, height
uniform float uDot;
uniform float uDpr;
uniform vec4 uFade;     // alpha near, alpha far, far fade, side fade
uniform vec4 uIntro;    // sweep front (0 .. 1 + softness), front softness, crest height, shape (0 flat .. 1)
uniform vec4 uSheetDrops[4]; // a click's point on the sheet: x, z, age s, alive (0 / 1)
uniform vec4 uSheetRipple;   // speed, width, height, life s
uniform vec3 uSheetHover;    // the pointer's point on the sheet: x, z, amount 0..1
uniform vec4 uSheetSwell;    // radius, height, light, grow
out float vAlpha;

float terrain(vec2 p, float t) {
  float h = 0.0;
  h += sin(p.x * 1.3 + t * 0.9) * cos(p.y * 1.1 - t * 0.7) * 0.55;
  h += sin(p.x * 2.7 - p.y * 1.9 + t * 1.3) * 0.25;
  h += sin(p.x * 4.1 + p.y * 3.3 - t * 1.7) * 0.12;
  h += cos(p.y * 5.2 + p.x * 0.8 + t * 2.1) * 0.08;
  return h;
}

void main() {
  float x = (aGrid.x - 0.5) * uSize.x;
  float z = uNear + aGrid.y * uSize.y;
  // both sides high, the middle sagging
  float side = pow(abs(aGrid.x - 0.5) * 2.0, uShape.y) * uShape.x;
  float dip = -uShape.z * mix(uDipNear, 1.0, aGrid.y) * exp(-x * x / max(uDipWidth * uDipWidth, 0.0001));
  // intro: rows appear from the near edge as the front passes, each rising and settling, while the sheet bends
  float reveal = clamp((uIntro.x - aGrid.y) / max(uIntro.y, 0.001), 0.0, 1.0);
  float y = (side + dip + terrain(vec2(x, z) * uRipple.y * 0.5, uTime) * uRipple.x) * uIntro.w;
  y += sin(reveal * 3.14159265) * uIntro.z;
  // the mouse: a soft mound under the pointer
  vec2 h = (vec2(x, z) - uSheetHover.xy) / uSheetSwell.x;
  float swell = exp(-dot(h, h)) * uSheetHover.z;
  y += swell * uSheetSwell.y;
  // clicks: one soft ring running out on the sheet, a rise then a fall
  for (int i = 0; i < 4; i++) {
    vec4 drop = uSheetDrops[i];
    if (drop.w < 0.5) continue;
    float s = (drop.z * uSheetRipple.x - length(vec2(x, z) - drop.xy)) / uSheetRipple.y;
    if (s < 0.0 || s > 1.0) continue;
    float life = pow(1.0 - clamp(drop.z / uSheetRipple.w, 0.0, 1.0), 2.0);
    y += sin(s * 6.2831853) * sin(s * 3.14159265) * life * uSheetRipple.z;
  }

  // camera above the plane looking slightly down
  vec3 v = vec3(x, y - uCam.y, z);
  float cs = cos(uCam.x);
  float sn = sin(uCam.x);
  vec3 cam = vec3(v.x, v.y * cs + v.z * sn, -v.y * sn + v.z * cs);
  if (cam.z < 0.05) { gl_Position = vec4(2.0, 2.0, 2.0, 1.0); vAlpha = 0.0; return; }
  float f = 1.6;
  vec2 ndc = vec2(cam.x * f / (cam.z * uAspect), cam.y * f / cam.z);

  gl_Position = vec4(ndc, 0.0, 1.0);
  gl_PointSize = max(1.0, uDot * uDpr * 2.4 / cam.z * (1.0 + swell * uSheetSwell.w));

  // opacity: near to far, then fading out at the far edge and both sides
  float far = 1.0 - smoothstep(1.0 - uFade.z, 1.0, aGrid.y);
  float sides = smoothstep(0.0, uFade.w, aGrid.x) * smoothstep(1.0, 1.0 - uFade.w, aGrid.x);
  vAlpha = min(1.0, mix(uFade.x, uFade.y, aGrid.y) * (1.0 + swell * uSheetSwell.z)) * far * sides * smoothstep(0.0, 1.0, reveal);
}`;

const DOT_FRAG = `#version 300 es
precision highp float;
in float vAlpha;
out vec4 outColor;
void main() {
  float d = length(gl_PointCoord - 0.5);
  float a = (1.0 - smoothstep(0.35, 0.5, d)) * vAlpha;
  if (a <= 0.002) discard;
  outColor = vec4(vec3(a), a);
}`;

function createWave(canvas: HTMLCanvasElement, area: HTMLElement, interactive: boolean, lite: boolean, band: () => Band): Wave | null {
	const gl = canvas.getContext('webgl2', { antialias: true, premultipliedAlpha: true });
	if (!gl) {
		return null;
	}

	const bg = createProgram(gl, BG_VERT, BG_FRAG);
	const dots = createProgram(gl, DOT_VERT, DOT_FRAG);

	const quadVao = gl.createVertexArray();
	gl.bindVertexArray(quadVao);
	gl.bindBuffer(gl.ARRAY_BUFFER, gl.createBuffer());
	gl.bufferData(gl.ARRAY_BUFFER, new Float32Array([-1, -1, 1, -1, -1, 1, 1, 1]), gl.STATIC_DRAW);
	const bgLoc = gl.getAttribLocation(bg, 'aPos');
	gl.enableVertexAttribArray(bgLoc);
	gl.vertexAttribPointer(bgLoc, 2, gl.FLOAT, false, 0, 0);

	const dotVao = gl.createVertexArray();
	const dotBuf = gl.createBuffer();
	const loc = gl.getAttribLocation(dots, 'aGrid');
	let count = 0;
	let gridKey = '';
	// the grid only changes when the tune panel changes the dot count
	const buildGrid = (): void => {
		const share = lite ? LITE.dots : 1;
		const cols = Math.max(2, Math.round(WAVE.cols * share));
		const rows = Math.max(2, Math.round(WAVE.rows * share));
		const key = `${cols}x${rows}`;
		if (key === gridKey) {
			return;
		}
		gridKey = key;
		const grid = new Float32Array(cols * rows * 2);
		let i = 0;
		for (let r = 0; r < rows; r++) {
			for (let c = 0; c < cols; c++) {
				grid[i++] = c / (cols - 1);
				grid[i++] = r / (rows - 1);
			}
		}
		count = cols * rows;
		gl.bindVertexArray(dotVao);
		gl.bindBuffer(gl.ARRAY_BUFFER, dotBuf);
		gl.bufferData(gl.ARRAY_BUFFER, grid, gl.STATIC_DRAW);
		gl.enableVertexAttribArray(loc);
		gl.vertexAttribPointer(loc, 2, gl.FLOAT, false, 0, 0);
	};

	const names = ['uTime', 'uAspect', 'uSize', 'uNear', 'uShape', 'uDipWidth', 'uDipNear', 'uRipple', 'uCam', 'uDot', 'uDpr', 'uFade', 'uIntro', 'uSheetDrops', 'uSheetRipple', 'uSheetHover', 'uSheetSwell'] as const;
	const u = Object.fromEntries(names.map((name) => [name, gl.getUniformLocation(dots, name)])) as Record<(typeof names)[number], WebGLUniformLocation | null>;

	// 16:9 frames covering a box (client px, from the canvas's top-left): ground = the whole canvas; dots = the same,
	// or on a portrait screen the band above the text. frame: the dots' top-left from the canvas's, client px (x, y)
	let dpr = 1;
	let ground: View = { x: 0, y: 0, w: 1, h: 1 };
	let view: View = ground;
	let frame = { x: 0, y: 0, w: 1, h: 1 };
	const cover = (top: number, width: number, height: number): View => {
		const w = Math.round(Math.max(width, (height * 16) / 9) * dpr);
		const h = Math.round(Math.max(height, (width * 9) / 16) * dpr);
		const x = Math.round((width * dpr - w) / 2);
		const fromTop = Math.round((top + height / 2) * dpr - h / 2);
		return { x, y: canvas.height - fromTop - h, w, h };
	};
	const resize = (): void => {
		dpr = Math.min(lite ? LITE.dpr : 2, window.devicePixelRatio);
		const r = canvas.getBoundingClientRect();
		canvas.width = Math.round(r.width * dpr);
		canvas.height = Math.round(r.height * dpr);
		ground = cover(0, r.width, r.height);
		const b = band();
		const bh = (b.bottom - b.top) * PORTRAIT.scale;
		view = r.height >= r.width && bh > 0 ? cover((b.top + b.bottom - bh) / 2, r.width, bh) : ground;
		frame = { x: view.x / dpr, y: (canvas.height - view.h - view.y) / dpr, w: view.w, h: view.h };
	};
	resize();

	// where a dot of the sheet lands on screen, device px in the frame: DOT_VERT's sheet and camera, without the pointer
	let time = 0; // the shader's uTime and shape at the last draw
	let shape = 1;
	const project = (gx: number, gy: number): [number, number] | null => {
		const W = WAVE;
		const x = (gx - 0.5) * (Math.round(W.cols) - 1) * W.gap;
		const z = W.near + gy * (Math.round(W.rows) - 1) * W.gap;
		const side = Math.pow(Math.abs(gx - 0.5) * 2, W.sidesCurve) * W.sides;
		const dip = -W.dip * (W.dipNear + (1 - W.dipNear) * gy) * Math.exp((-x * x) / Math.max(W.dipWidth * W.dipWidth, 0.0001));
		const px = x * W.freq * 0.5;
		const pz = z * W.freq * 0.5;
		const terrain =
			Math.sin(px * 1.3 + time * 0.9) * Math.cos(pz * 1.1 - time * 0.7) * 0.55 +
			Math.sin(px * 2.7 - pz * 1.9 + time * 1.3) * 0.25 +
			Math.sin(px * 4.1 + pz * 3.3 - time * 1.7) * 0.12 +
			Math.cos(pz * 5.2 + px * 0.8 + time * 2.1) * 0.08;
		const vy = (side + dip + terrain * W.amp) * shape - W.camY;
		const cs = Math.cos(W.tilt);
		const sn = Math.sin(W.tilt);
		const cy = vy * cs + z * sn;
		const cz = -vy * sn + z * cs;
		if (cz < 0.05) {
			return null;
		}
		const f = 1.6 / cz;
		return [((x * f) / (frame.w / Math.max(1, frame.h)) * 0.5 + 0.5) * frame.w, (0.5 - cy * f * 0.5) * frame.h];
	};
	// the sheet's point under the pointer (grid 0..1): the nearest of a coarse grid, then two finer passes round it;
	// from near (the last frame's point) the two finer passes alone
	const pick = (clientX: number, clientY: number, near: [number, number] | null = null): [number, number] | null => {
		const box = canvas.getBoundingClientRect();
		const tx = (clientX - box.left - frame.x) * dpr;
		const ty = (clientY - box.top - frame.y) * dpr;
		let best: [number, number] | null = null;
		let bestD = Infinity;
		const search = (cx: number, cy: number, stride: number, n: number): void => {
			for (let i = -n; i <= n; i++) {
				for (let j = -n; j <= n; j++) {
					const gx = cx + i * stride;
					const gy = cy + j * stride;
					const p = gx >= 0 && gx <= 1 && gy >= 0 && gy <= 1 ? project(gx, gy) : null;
					const d = p ? Math.hypot(p[0] - tx, p[1] - ty) : Infinity;
					if (d < bestD) {
						bestD = d;
						best = [gx, gy];
					}
				}
			}
		};
		if (near) {
			search(near[0], near[1], 1 / 320, 6);
		} else {
			search(0.5, 0.5, 1 / 80, 40);
		}
		for (const stride of [1 / 320, 1 / 1280]) {
			if (best) {
				search(best[0], best[1], stride, 4);
			}
		}
		if (bestD > SHEET_RIPPLE.pick * dpr) {
			return near ? pick(clientX, clientY) : null;
		}
		return best;
	};
	const world = (at: [number, number]): [number, number] => [(at[0] - 0.5) * (Math.round(WAVE.cols) - 1) * WAVE.gap, WAVE.near + at[1] * (Math.round(WAVE.rows) - 1) * WAVE.gap];
	const drops: { x: number; z: number; age: number }[] = []; // world units
	const addDrop = (clientX: number, clientY: number): void => {
		const at = pick(clientX, clientY);
		if (!at) {
			return;
		}
		const [x, z] = world(at);
		drops.push({ x, z, age: 0 });
		if (drops.length > DOTS.maxDrops) {
			drops.shift();
		}
	};

	// the pointer (assets/js/dot-ripple.ts, eased and faded as every dot background's) and the clicks; none while still
	// or without a mouse
	const stir = interactive ? dotStir(area, canvas, () => ({ x: frame.x, y: frame.y, dpr }), { onDrop: addDrop }) : null;
	// the swell: the pointer's point on the sheet, found again each frame (the sheet moves under a still mouse); off the
	// sheet it fades where it was. A pointer above the sheet is searched again only once it moves (the full search)
	const swell = { at: null as [number, number] | null, x: 0, z: 0, amount: 0, missX: Infinity, missY: Infinity };
	const followSwell = (dt: number): void => {
		const p = stir?.pointer();
		let on = false;
		if (p && p.amount > 0 && (swell.at || Math.hypot(p.x - swell.missX, p.y - swell.missY) > 2)) {
			swell.at = pick(p.x, p.y, swell.at);
			if (swell.at) {
				[swell.x, swell.z] = world(swell.at);
				on = true;
			} else {
				swell.missX = p.x;
				swell.missY = p.y;
			}
		}
		const fade = 1 - Math.pow(1 - DOTS.hoverFade, dt * 60);
		swell.amount += ((on && p ? p.amount : 0) - swell.amount) * fade;
	};

	// intro: 0 .. 1 progress of the sweep (1 = done)
	const draw = (seconds: number, intro = 1, dt = 0): void => {
		buildGrid();
		const W = WAVE;
		stir?.update(dt);
		followSwell(dt);
		drops.forEach((drop) => (drop.age += dt));
		while (drops.length && drops[0].age > SHEET_RIPPLE.life) {
			drops.shift();
		}
		gl.viewport(ground.x, ground.y, ground.w, ground.h);
		gl.disable(gl.BLEND);
		gl.useProgram(bg);
		gl.bindVertexArray(quadVao);
		gl.drawArrays(gl.TRIANGLE_STRIP, 0, 4);

		gl.viewport(view.x, view.y, view.w, view.h);
		gl.enable(gl.BLEND);
		gl.blendFunc(gl.ONE, gl.ONE_MINUS_SRC_ALPHA);
		gl.useProgram(dots);
		gl.uniform1f(u.uTime, seconds * W.speed);
		gl.uniform1f(u.uAspect, frame.w / Math.max(1, frame.h));
		gl.uniform2f(u.uSize, (Math.round(W.cols) - 1) * W.gap, (Math.round(W.rows) - 1) * W.gap);
		gl.uniform1f(u.uNear, W.near);
		gl.uniform3f(u.uShape, W.sides, W.sidesCurve, W.dip);
		gl.uniform1f(u.uDipWidth, W.dipWidth);
		gl.uniform1f(u.uDipNear, W.dipNear);
		gl.uniform2f(u.uRipple, W.amp, W.freq);
		gl.uniform2f(u.uCam, W.tilt, W.camY);
		gl.uniform1f(u.uDot, W.dot * (view.h / dpr / 1080)); // the hero-wide size, also in the portrait band
		gl.uniform1f(u.uDpr, dpr);
		const alpha = view === ground ? 1 : PORTRAIT.alpha;
		gl.uniform4f(u.uFade, W.alphaNear * alpha, W.alphaFar * alpha, W.fadeFar, W.fadeSides);
		const e = easeOut(intro, 2);
		gl.uniform4f(u.uIntro, e * (1 + INTRO.gridSoft), INTRO.gridSoft, INTRO.gridCrest, e);
		time = seconds * W.speed;
		shape = e;
		const sheet = new Float32Array(16);
		drops.forEach((drop, i) => sheet.set([drop.x, drop.z, drop.age, 1], i * 4));
		gl.uniform4fv(u.uSheetDrops, sheet);
		gl.uniform4f(u.uSheetRipple, SHEET_RIPPLE.speed, SHEET_RIPPLE.width, SHEET_RIPPLE.height, SHEET_RIPPLE.life);
		gl.uniform3f(u.uSheetHover, swell.x, swell.z, swell.amount);
		gl.uniform4f(u.uSheetSwell, SHEET_HOVER.radius, SHEET_HOVER.height, SHEET_HOVER.light, SHEET_HOVER.grow);
		gl.bindVertexArray(dotVao);
		gl.drawArrays(gl.POINTS, 0, count);
	};

	return { resize, draw };
}

/*
 * ?tune: a slider for every WAVE and INTRO value, for the owner to tune the grid and the intro by eye.
 * Dev only (nothing is built without ?tune). Values stay in this browser; "Copy" puts both objects on the clipboard
 * to paste above; "Replay" plays the intro again. Styles inline so no CSS ships for it.
 */
type Range = [min: number, max: number, step: number, label: string];
const TUNE: Record<keyof typeof WAVE, Range> = {
	cols: [40, 600, 1, 'Columns'],
	rows: [20, 400, 1, 'Rows'],
	gap: [0.005, 0.1, 0.001, 'Gap'],
	near: [0.2, 5, 0.01, 'Near edge distance'],
	dot: [0.5, 8, 0.1, 'Dot size px'],
	alphaNear: [0, 1, 0.01, 'Opacity near'],
	alphaFar: [0, 1, 0.01, 'Opacity far'],
	fadeFar: [0, 1, 0.01, 'Far fade'],
	fadeSides: [0, 0.5, 0.01, 'Side fade'],
	sides: [-1, 3, 0.01, 'Sides height'],
	sidesCurve: [0.5, 6, 0.1, 'Sides curve'],
	dip: [-1, 2, 0.01, 'Middle dip'],
	dipNear: [0, 1, 0.01, 'Middle dip at the near edge (share)'],
	dipWidth: [0.1, 5, 0.01, 'Dip width'],
	amp: [0, 1, 0.01, 'Ripple height'],
	freq: [0.2, 6, 0.05, 'Ripple scale'],
	speed: [0, 2, 0.01, 'Ripple speed'],
	tilt: [0, 1.2, 0.01, 'Camera tilt'],
	camY: [0, 4, 0.01, 'Camera height'],
};
const TUNE_INTRO: Record<keyof typeof INTRO, Range> = {
	grid: [0.2, 5, 0.05, 'Intro: grid sweep s'],
	gridSoft: [0.02, 1, 0.01, 'Intro: sweep front depth'],
	gridCrest: [0, 1, 0.01, 'Intro: row rise'],
	gap: [-2, 2, 0.05, 'Intro: hand start after grid s'],
	hand: [0.2, 5, 0.05, 'Intro: hand fly-in s'],
	handSpread: [0, 0.9, 0.01, 'Intro: hand stagger'],
	handScatter: [0, 200, 1, 'Intro: hand scatter'],
};
const TUNE_KEY = 'hola-hero-wave-tune';
const TUNE_INTRO_KEY = 'hola-hero-intro-tune';

function tunePanel(replay: () => void): void {
	try {
		Object.assign(WAVE, JSON.parse(localStorage.getItem(TUNE_KEY) || '{}'));
		Object.assign(INTRO, JSON.parse(localStorage.getItem(TUNE_INTRO_KEY) || '{}'));
	} catch {
		// private mode or a broken value: the defaults stay
	}
	const save = (): void => {
		try {
			localStorage.setItem(TUNE_KEY, JSON.stringify(WAVE));
			localStorage.setItem(TUNE_INTRO_KEY, JSON.stringify(INTRO));
		} catch {
			// private mode: values last until the reload
		}
	};
	const panel = document.createElement('div');
	panel.style.cssText = 'position:fixed;top:96px;right:16px;z-index:9999;width:280px;max-height:calc(100vh - 112px);overflow:auto;padding:12px;border-radius:12px;background:rgb(15 18 26 / 0.92);color:#fff;font:12px/1.3 system-ui,sans-serif;box-shadow:0 8px 24px rgb(0 0 0 / 0.3)';
	panel.setAttribute('data-lenis-prevent', '');
	const head = document.createElement('div');
	head.style.cssText = 'display:flex;gap:6px;align-items:center;margin-bottom:8px';
	head.innerHTML = '<strong style="flex:1">Hero</strong>';
	const button = (label: string, onClick: () => void): HTMLButtonElement => {
		const b = document.createElement('button');
		b.type = 'button';
		b.textContent = label;
		b.style.cssText = 'padding:4px 10px;border:0;border-radius:6px;background:rgb(255 255 255 / 0.12);color:#fff;font:inherit;cursor:pointer;transition:background-color var(--duration-hover)';
		b.addEventListener('mouseenter', () => (b.style.background = '#0048f9'));
		b.addEventListener('mouseleave', () => (b.style.background = 'rgb(255 255 255 / 0.12)'));
		b.addEventListener('click', onClick);
		head.append(b);
		return b;
	};
	const inputs: Array<[Record<string, number>, string, HTMLInputElement, HTMLSpanElement]> = [];
	button('Replay', () => replay());
	const copy = button('Copy', () => {
		const object = (name: string, o: Record<string, number>): string => `const ${name} = {\n${Object.keys(o).map((k) => `\t${k}: ${+o[k].toFixed(4)},`).join('\n')}\n};`;
		void navigator.clipboard?.writeText(`${object('WAVE', WAVE)}\n${object('INTRO', INTRO)}`).then(() => {
			copy.textContent = 'Copied';
			setTimeout(() => (copy.textContent = 'Copy'), 1200);
		});
	});
	button('Reset', () => {
		Object.assign(WAVE, WAVE_DEFAULTS);
		Object.assign(INTRO, INTRO_DEFAULTS);
		inputs.forEach(([target, key, range, out]) => {
			range.value = String(target[key]);
			out.textContent = String(target[key]);
		});
		save();
	});
	panel.append(head);
	const rows: Array<[Record<string, number>, string, Range]> = [
		...Object.entries(TUNE).map(([key, range]): [Record<string, number>, string, Range] => [WAVE, key, range]),
		...Object.entries(TUNE_INTRO).map(([key, range]): [Record<string, number>, string, Range] => [INTRO, key, range]),
	];
	rows.forEach(([target, key, [min, max, stride, label]]) => {
		const row = document.createElement('label');
		row.style.cssText = 'display:grid;grid-template-columns:1fr auto;gap:2px 8px;margin-top:6px';
		const out = document.createElement('span');
		out.style.cssText = 'font-variant-numeric:tabular-nums;opacity:0.7';
		out.textContent = String(target[key]);
		const range = document.createElement('input');
		Object.assign(range, { type: 'range', min: String(min), max: String(max), step: String(stride), value: String(target[key]) });
		range.style.cssText = 'grid-column:1 / -1;width:100%;accent-color:#4ac9ff;cursor:pointer';
		range.addEventListener('input', () => {
			target[key] = +range.value;
			out.textContent = range.value;
			save();
		});
		row.append(label, out, range);
		panel.append(row);
		inputs.push([target, key, range, out]);
	});
	document.body.append(panel);
}

/* ------------------------------------------------------------------
 * 2. Particle mark: the source's ParticleMark, as is. The Figma "Shape-based particles" effect on each stroke of
 * the logo, ported to a CPU spring simulation + WebGL2 points, white dots. Panel values from Figma; density, dot
 * scale and strength tuned by eye against the design.
 * ------------------------------------------------------------------ */
const P = {
	density: 59, // %
	dotScale: 57, // %
	radius: 220, // mouse radius, box units
	strength: 19, // %
	spring: 33, // %
	damping: 50, // %
	mouseScale: 222, // %
	zOffset: 150,
};

const STROKE = 8; // the vector's outside stroke
const gridFor = (density: number): number => Math.max(2, Math.min(32, 400 / (10 + density * 1.9)));

function derive() {
	const strength = P.strength * 6;
	const springPct = P.spring * 3;
	const spring = springPct <= 100 ? springPct * 0.01 : 1 + (springPct - 100) * 0.02;
	const retention = 1 - P.damping * 0.00375;
	const baseStiff = 300 / (14 * 8); // physics calibrated to an 8px grid
	return {
		radius: P.radius,
		strength,
		strengthScale: strength / 300,
		spring,
		springMix: Math.min(1, Math.max(0, spring)),
		retention,
		baseStiff,
		stiff: baseStiff * spring,
		radiusScale: Math.min(1, P.radius / 400),
		drag: Math.min(1.5, Math.max(0, (1 - retention) * 4)),
		simZ: P.zOffset * 0.85,
		zOffset: P.zOffset,
		perspective: Math.max(350, 1200 - P.zOffset * 4),
		dotRadius: gridFor(P.density) * (P.dotScale / 100) * 0.5,
		mouseScale: P.mouseScale,
	};
}

// "Wave" mouse falloff: three concentric pulses inside a soft envelope, never negative
const smooth = (a: number, b: number, x: number): number => {
	const t = Math.min(1, Math.max(0, (x - a) / (b - a)));
	return t * t * (3 - 2 * t);
};
const falloff = (dist: number, radius: number): number => {
	const t = Math.min(1, Math.max(0, dist / Math.max(radius, 1)));
	return (1 - smooth(0, 1, t)) * (0.15 + 0.85 * (0.5 + 0.5 * Math.cos(6 * Math.PI * t)));
};
const followAmount = (dt: number, drag: number): number => {
	if (dt <= 0) {
		return 0;
	}
	if (drag <= 0.0001) {
		return 1;
	}
	return 1 - Math.pow(Math.max(1 - Math.pow(2, -4 * drag), 0), dt * 60);
};
const hash2 = (x: number, y: number): [number, number] => {
	const a = Math.sin(x * 127.1 + y * 311.7) * 43758.5453;
	const b = Math.sin(x * 269.5 + y * 183.3) * 43758.5453;
	return [a - Math.floor(a), b - Math.floor(b)];
};

function raster(box: number): CanvasRenderingContext2D {
	const c = document.createElement('canvas');
	c.width = box;
	c.height = box;
	return c.getContext('2d', { willReadFrequently: true }) as CanvasRenderingContext2D;
}

function bounds(path: Path2D, box: number) {
	const ctx = raster(box);
	ctx.fill(path);
	const a = ctx.getImageData(0, 0, box, box).data;
	let x0 = box;
	let y0 = box;
	let x1 = 0;
	let y1 = 0;
	for (let y = 0; y < box; y++) {
		for (let x = 0; x < box; x++) {
			if (!a[(y * box + x) * 4 + 3]) {
				continue;
			}
			x0 = Math.min(x0, x);
			y0 = Math.min(y0, y);
			x1 = Math.max(x1, x + 1);
			y1 = Math.max(y1, y + 1);
		}
	}
	return { x0: x0 - STROKE, y0: y0 - STROKE, x1: x1 + STROKE, y1: y1 + STROKE };
}

type Field = {
	box: number;
	count: number;
	intro: number; // 0 .. 1 progress of the fly-in (1 = done)
	scatter: Float32Array;
	delay: Float32Array;
	rest: Float32Array;
	center: Float32Array;
	layer: Float32Array;
	disp: Float32Array;
	vel: Float32Array;
	z: Float32Array; // zVel, zDisp, respVel, resp
	out: Float32Array; // projected x, y, radius
};

/**
 * A jittered grid over each vector's bounds (stroke included). Inverse Alpha at threshold 100% keeps a cell only
 * where the vector's pixels are fully transparent: inside its hidden fill, or beyond its stroke.
 */
function sampleField(paths: string[], box: number): Field {
	const GRID = gridFor(P.density);
	const rest: number[] = [];
	const center: number[] = [];
	const layer: number[] = [];
	paths.forEach((d, li) => {
		const path = new Path2D(d);
		const b = bounds(path, box);
		const ctx = raster(box);
		ctx.lineWidth = STROKE * 2;
		ctx.stroke(path);
		ctx.globalCompositeOperation = 'destination-out';
		ctx.fill(path);
		const a = ctx.getImageData(0, 0, box, box).data;
		const fill = raster(box);
		fill.fill(path);
		const inner = fill.getImageData(0, 0, box, box).data;
		const w = b.x1 - b.x0;
		const h = b.y1 - b.y0;
		const cols = Math.ceil(w / GRID);
		const rows = Math.ceil(h / GRID);
		for (let row = 0; row < rows; row++) {
			for (let col = 0; col < cols; col++) {
				const [hx, hy] = hash2(col, row);
				const lx = (col + hx) * GRID;
				const ly = (row + hy) * GRID;
				if (lx >= w || ly >= h) {
					continue;
				}
				const x = b.x0 + lx;
				const y = b.y0 + ly;
				const px = Math.floor(x);
				const py = Math.floor(y);
				const inBox = px >= 0 && py >= 0 && px < box && py < box;
				if (inBox && a[(py * box + px) * 4 + 3] > 0) {
					continue;
				}
				rest.push(x, y);
				center.push(b.x0 + w / 2, b.y0 + h / 2);
				// + 4 when the rest point is inside the shape: only those show while the intro has the mask off
				layer.push(li + (inBox && inner[(py * box + px) * 4 + 3] > 0 ? 4 : 0));
			}
		}
	});
	const count = layer.length;
	// intro: each particle starts up to handScatter away from home and leaves at its own delay
	const scatter = new Float32Array(count * 2);
	const delay = new Float32Array(count);
	for (let i = 0; i < count; i++) {
		const angle = Math.random() * Math.PI * 2;
		const dist = 0.3 + 0.7 * Math.random(); // times INTRO.handScatter in step()
		scatter[i * 2] = Math.cos(angle) * dist;
		scatter[i * 2 + 1] = Math.sin(angle) * dist;
		delay[i] = Math.random();
	}
	return {
		box,
		count,
		intro: 1,
		scatter,
		delay,
		rest: new Float32Array(rest),
		center: new Float32Array(center),
		layer: new Float32Array(layer),
		disp: new Float32Array(count * 2),
		vel: new Float32Array(count * 2),
		z: new Float32Array(count * 4),
		out: new Float32Array(count * 3),
	};
}

type Pointer = { x: number; y: number; px: number; py: number; valid: boolean };

/** One frame of the spring simulation, then the perspective projection. Returns the largest dot move, box units. */
function step(f: Field, m: Pointer, dt: number): number {
	const c = derive();
	let most = 0;
	const mx = m.valid ? m.x : -9999;
	const my = m.valid ? m.y : -9999;
	const pmx = m.valid ? m.px : mx;
	const pmy = m.valid ? m.py : my;
	const segX = mx - pmx;
	const segY = my - pmy;
	const moved = m.valid && segX * segX + segY * segY > 0.25;
	const segLen2 = Math.max(segX * segX + segY * segY, 0.0001);
	const r2 = c.radius * c.radius;

	for (let i = 0; i < f.count; i++) {
		const i2 = i * 2;
		const i4 = i * 4;
		const rx = f.rest[i2];
		const ry = f.rest[i2 + 1];
		let vx = f.vel[i2];
		let vy = f.vel[i2 + 1];
		let dx = f.disp[i2];
		let dy = f.disp[i2 + 1];
		let zVel = f.z[i4];
		let zDisp = f.z[i4 + 1];
		let respVel = f.z[i4 + 2];
		let resp = f.z[i4 + 3];

		// 8 substeps along this frame's pointer path when it passed near the cell
		let steps = 1;
		if (moved) {
			const t = Math.min(1, Math.max(0, ((rx - pmx) * segX + (ry - pmy) * segY) / segLen2));
			const qx = rx - (pmx + segX * t);
			const qy = ry - (pmy + segY * t);
			if (qx * qx + qy * qy < r2) {
				steps = 8;
			}
		}
		const sub = dt / steps;
		const keep = Math.pow(c.retention, 1 / steps);
		const follow = followAmount(sub, c.drag);
		const k = sub * 60;

		for (let s = 1; s <= steps; s++) {
			const tx = rx - (pmx + (segX * s) / steps);
			const ty = ry - (pmy + (segY * s) / steps);
			const dist = Math.hypot(tx, ty);
			let target = 0;
			let tdx = 0;
			let tdy = 0;
			if (dist < c.radius) {
				target = falloff(dist, c.radius);
				if (dist > 0.001) {
					const force = (target * c.strength * c.radiusScale) / c.baseStiff / dist;
					tdx = tx * force;
					tdy = ty * force;
				}
			}

			vx = (vx + (tdx - dx) * c.stiff * k) * keep;
			vy = (vy + (tdy - dy) * c.stiff * k) * keep;
			const gdx = dx + (tdx - dx) * follow;
			const gdy = dy + (tdy - dy) * follow;
			dx = gdx + (dx + vx * sub - gdx) * c.springMix;
			dy = gdy + (dy + vy * sub - gdy) * c.springMix;
			vx *= c.springMix;
			vy *= c.springMix;

			zVel += -c.stiff * zDisp * k;
			zVel += (-c.simZ * target - zDisp) * 8 * c.strengthScale * c.spring * k * target;
			zVel *= keep;
			const springZ = zDisp + zVel * sub;
			respVel = (respVel + (target - resp) * c.stiff * k) * keep;
			const springResp = resp + respVel * sub;
			const dragZ = zDisp + (-c.simZ * target * c.strengthScale - zDisp) * follow;
			const dragResp = resp + (target - resp) * follow;
			zDisp = dragZ + (springZ - dragZ) * c.springMix;
			resp = dragResp + (springResp - dragResp) * c.springMix;
			zVel *= c.springMix;
			respVel *= c.springMix;
		}

		f.vel[i2] = vx;
		f.vel[i2 + 1] = vy;
		f.disp[i2] = dx;
		f.disp[i2 + 1] = dy;
		f.z[i4] = zVel;
		f.z[i4 + 1] = zDisp;
		f.z[i4 + 2] = respVel;
		f.z[i4 + 3] = resp;

		// perspective about the owning vector's centre; lifted dots grow
		const influence = Math.min(1, Math.max(0, resp));
		const cx = f.center[i2];
		const cy = f.center[i2 + 1];
		const effZ = zDisp - c.zOffset * influence * c.strengthScale * 0.15;
		const denom = Math.max(c.perspective + effZ, 1);
		const i3 = i * 3;
		const ox = (c.perspective * (rx - cx + dx)) / denom + cx;
		const oy = (c.perspective * (ry - cy + dy)) / denom + cy;
		const or = ((c.dotRadius * c.perspective) / denom) * Math.max(0.01, 1 + influence * c.mouseScale * 0.01);
		most = Math.max(most, Math.abs(ox - f.out[i3]), Math.abs(oy - f.out[i3 + 1]), Math.abs(or - f.out[i3 + 2]));
		f.out[i3] = ox;
		f.out[i3 + 1] = oy;
		f.out[i3 + 2] = or;

		// intro: fly in from the scattered start, growing from nothing (power3.out per particle)
		if (f.intro < 1) {
			const spread = Math.min(0.9, INTRO.handSpread);
			const e = easeOut((f.intro - f.delay[i] * spread) / (1 - spread), 3);
			f.out[i3] += f.scatter[i2] * INTRO.handScatter * (1 - e);
			f.out[i3 + 1] += f.scatter[i2 + 1] * INTRO.handScatter * (1 - e);
			f.out[i3 + 2] *= e;
			// not started yet: off the canvas, since even a 0 radius dot draws its 1px feather
			if (e < 0.001) {
				f.out[i3] = -9999;
			}
		}
	}
	return most;
}

const MARK_VERT = `#version 300 es
in vec3 aOut;     // x, y, radius in box units
in float aLayer;   // vector index, + 4 when the rest point is inside the shape
uniform float uBox;
uniform float uPx; // device px per box unit
out float vRadius;
out float vIn;
flat out int vLayer;
void main() {
  vec2 clip = aOut.xy / uBox * 2.0 - 1.0;
  gl_Position = vec4(clip.x, -clip.y, 0.0, 1.0);
  gl_PointSize = (aOut.z + 2.0) * 2.0 * uPx;
  vRadius = aOut.z;
  int code = int(aLayer + 0.5);
  vLayer = code % 4;
  vIn = float(code / 4);
}`;

const MARK_FRAG = `#version 300 es
precision highp float;
in float vRadius;
in float vIn;
flat in int vLayer;
uniform vec2 uView;
uniform float uMaskMix; // intro: 0 = no clipping (particles fly in from outside), 1 = clipped to the shape
uniform sampler2D uMask; // r, g, b = interior of vector 0, 1, 2
out vec4 outColor;
void main() {
  vec2 local = (gl_PointCoord - 0.5) * (vRadius + 2.0) * 2.0;
  // softness 0%: a fixed 1px feather (box units)
  float edge = 1.0 - smoothstep(vRadius - 1.0, vRadius + 1.0, length(local));
  if (edge < 0.001) discard;
  // a vector's particles only show inside that vector's own shape
  vec2 uv = vec2(gl_FragCoord.x, uView.y - gl_FragCoord.y) / uView;
  vec3 mask = texture(uMask, uv).rgb;
  float inside = vLayer == 0 ? mask.r : (vLayer == 1 ? mask.g : mask.b);
  float a = edge * mix(vIn, inside, uMaskMix);
  outColor = vec4(vec3(a), a); // white, premultiplied; coverage sums then clamps
}`;

function createMark(canvas: HTMLCanvasElement, field: Field, paths: string[]): Mark | null {
	const gl = canvas.getContext('webgl2', { premultipliedAlpha: true, antialias: false, alpha: true });
	if (!gl) {
		return null;
	}
	const prog = createProgram(gl, MARK_VERT, MARK_FRAG);
	gl.useProgram(prog);

	const vao = gl.createVertexArray();
	gl.bindVertexArray(vao);
	const outBuf = gl.createBuffer();
	gl.bindBuffer(gl.ARRAY_BUFFER, outBuf);
	gl.bufferData(gl.ARRAY_BUFFER, field.out, gl.DYNAMIC_DRAW);
	const locOut = gl.getAttribLocation(prog, 'aOut');
	gl.enableVertexAttribArray(locOut);
	gl.vertexAttribPointer(locOut, 3, gl.FLOAT, false, 0, 0);
	gl.bindBuffer(gl.ARRAY_BUFFER, gl.createBuffer());
	gl.bufferData(gl.ARRAY_BUFFER, field.layer, gl.STATIC_DRAW);
	const locLayer = gl.getAttribLocation(prog, 'aLayer');
	gl.enableVertexAttribArray(locLayer);
	gl.vertexAttribPointer(locLayer, 1, gl.FLOAT, false, 0, 0);

	const u = (n: string) => gl.getUniformLocation(prog, n);
	gl.uniform1f(u('uBox'), field.box);
	gl.uniform1i(u('uMask'), 0);
	const uPx = u('uPx');
	const uView = u('uView');
	const uMaskMix = u('uMaskMix');

	// premultiplied white added up and saturated = the effect's clamped coverage
	gl.enable(gl.BLEND);
	gl.blendFunc(gl.ONE, gl.ONE);

	const tex = gl.createTexture();
	const buildMask = (px: number): void => {
		const c = document.createElement('canvas');
		c.width = px;
		c.height = px;
		const ctx = c.getContext('2d') as CanvasRenderingContext2D;
		ctx.scale(px / field.box, px / field.box);
		ctx.globalCompositeOperation = 'lighter';
		['#f00', '#0f0', '#00f'].forEach((color, i) => {
			ctx.fillStyle = color;
			ctx.fill(new Path2D(paths[i]));
		});
		gl.activeTexture(gl.TEXTURE0);
		gl.bindTexture(gl.TEXTURE_2D, tex);
		gl.texImage2D(gl.TEXTURE_2D, 0, gl.RGBA, gl.RGBA, gl.UNSIGNED_BYTE, c);
		gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_MIN_FILTER, gl.LINEAR);
		gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_MAG_FILTER, gl.LINEAR);
		gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_WRAP_S, gl.CLAMP_TO_EDGE);
		gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_WRAP_T, gl.CLAMP_TO_EDGE);
	};

	return {
		resize(cssSize: number): void {
			const px = Math.max(1, Math.round(cssSize * Math.min(2, window.devicePixelRatio)));
			canvas.width = px;
			canvas.height = px;
			gl.viewport(0, 0, px, px);
			gl.uniform1f(uPx, px / field.box);
			gl.uniform2f(uView, px, px);
			buildMask(px);
		},
		draw(maskMix = 1): void {
			gl.uniform1f(uMaskMix, maskMix);
			gl.bindBuffer(gl.ARRAY_BUFFER, outBuf);
			gl.bufferSubData(gl.ARRAY_BUFFER, 0, field.out);
			gl.clearColor(0, 0, 0, 0);
			gl.clear(gl.COLOR_BUFFER_BIT);
			gl.drawArrays(gl.POINTS, 0, field.count);
		},
	};
}

/* ------------------------------------------------------------------
 * 3. Wiring: one rAF loop per hero, paused while the hero is off screen or the tab is hidden.
 * ------------------------------------------------------------------ */
// after the intro, the average time between drawn frames over this many frames; over budget (under ~25 fps, phones
// draw at 30) the hero stops on its current frame
const SLOW = { frames: 30, budget: 40 }; // budget: ms

function init(hero: HTMLElement, software: boolean): void {
	if (hero.dataset.heroReady) {
		return;
	}
	hero.dataset.heroReady = 'true';

	const waveCanvas = hero.querySelector<HTMLCanvasElement>('[data-hero-wave]');
	const markBox = hero.querySelector<HTMLElement>('[data-hero-mark]');
	const markCanvas = hero.querySelector<HTMLCanvasElement>('[data-hero-particles]');
	const markSvg = markBox?.querySelector('svg');
	if (!waveCanvas || !markBox || !markCanvas || !markSvg) {
		return;
	}
	// portrait: the dots' band, from 16px under the header to the top of the text's frosted strip (hero.css, 48px
	// above the text)
	const inner = hero.querySelector<HTMLElement>('.hero__inner');
	const band = (): Band => {
		const top = hero.getBoundingClientRect().top;
		const header = parseFloat(getComputedStyle(document.documentElement).getPropertyValue('--px-header-height')) || 80;
		return { top: header + 16, bottom: inner ? inner.getBoundingClientRect().top - top - 48 : 0 };
	};
	const paths = [...markBox.querySelectorAll('defs path')].map((p) => p.getAttribute('d') || '');
	const box = markSvg.viewBox.baseVal.width;
	let still = isStill();
	if (!still && software) {
		still = true;
		console.info('[hero] software WebGL: still hero');
	}
	const mouseDevice = window.matchMedia('(hover: hover) and (pointer: fine)').matches;
	const hasMouse = dotsPlay(); // the pointer play, as every dot background: a mouse, not ?still or reduced motion

	let wave: Wave | null = null;
	let mark: Mark | null = null;
	let field: Field | null = null;
	try {
		wave = createWave(waveCanvas, hero, hasMouse, !mouseDevice, band);
		if (wave && new URLSearchParams(window.location.search).has('tune')) {
			tunePanel(() => replay());
		}
		field = sampleField(paths, box);
		mark = createMark(markCanvas, field, paths);
	} catch (e) {
		// no WebGL2 or a driver error: the static SVG mark and the brand background stay
		console.warn('[hero] WebGL disabled:', (e as Error).message);
	}

	// intro: grid, then hand; the text waits for "px:hero-intro" (assets/js/fade-in.ts, data-px-fade-in-after)
	let introTime = still ? Infinity : 0;
	let introDone = false;
	let announced = false;
	// the mark's dots settled and the pointer still: no simulation, the canvas keeps its last frame
	let resting = false;
	const showMark = (amount: number): void => {
		markBox.style.animation = 'none'; // the CSS fallback that shows the mark if this script never runs
		markBox.style.opacity = '1';
		markSvg.style.opacity = amount >= 1 ? '' : String(amount);
	};
	const finishIntro = (): void => {
		introDone = true;
		if (announced) {
			return;
		}
		announced = true;
		document.documentElement.setAttribute('data-px-hero-intro', '');
		document.dispatchEvent(new Event('px:hero-intro'));
	};
	const replay = (): void => {
		introTime = 0;
		introDone = false;
		resting = false;
		if (field) {
			field.intro = 0;
		}
	};
	if (!wave && !mark) {
		showMark(1);
		finishIntro();
		return;
	}

	// pointer in box units; the effect sweeps from last frame's position to this one
	const mouse: Pointer = { x: 0, y: 0, px: 0, py: 0, valid: false };
	let next = { x: 0, y: 0, valid: false };
	let rect = markBox.getBoundingClientRect();

	// ?still / reduced motion: one frame, again on resize; nothing follows the pointer
	const drawStill = (): void => {
		wave?.draw(0, 1, 0);
		if (mark && field) {
			field.intro = 1;
			step(field, mouse, 0);
			mark.draw(1);
		}
	};

	const resize = (): void => {
		// the room above the text caps the hand's size on short screens (hero.css, round 1 #22)
		if (inner) {
			const room = inner.getBoundingClientRect().top - hero.getBoundingClientRect().top;
			hero.style.setProperty('--mark-room', `${Math.round(room)}px`);
		}
		rect = markBox.getBoundingClientRect();
		wave?.resize();
		mark?.resize(rect.width);
		resting = false; // the resize cleared the canvas
		if (still) {
			drawStill();
		}
	};
	resize();
	window.addEventListener('resize', resize);
	// the text's height (fonts, wrapping) moves the dots' band
	if (inner) {
		new ResizeObserver(resize).observe(inner);
	}

	if (still) {
		showMark(1);
		finishIntro();
		return;
	}

	if (hasMouse) {
		window.addEventListener(
			'pointermove',
			(e) => {
				if (e.pointerType !== 'mouse' || !field) {
					return;
				}
				const k = field.box / rect.width;
				next = { x: (e.clientX - rect.left) * k, y: (e.clientY - rect.top) * k, valid: true };
			},
			{ passive: true }
		);
		// off the page the pointer is invalid and every dot springs home
		document.documentElement.addEventListener('pointerleave', () => {
			next = { ...next, valid: false };
		});
		window.addEventListener('scroll', () => (rect = markBox.getBoundingClientRect()), { passive: true });
	}

	let raf = 0;
	let visible = true;
	let last = performance.now();
	const start = last;
	const frameTime = mouseDevice ? 0 : 1000 / LITE.fps - 3; // ms; a little early so a 60 Hz screen draws every other frame
	let timed = 0;
	let spent = 0;
	const tick = (now: number): void => {
		if (introDone && now - last < frameTime) {
			raf = requestAnimationFrame(tick);
			return;
		}
		// the frame-time guard, once after the intro
		if (introDone && timed < SLOW.frames) {
			spent += now - last;
			timed++;
			if (timed === SLOW.frames && spent / SLOW.frames > SLOW.budget) {
				console.info(`[hero] ${Math.round(spent / SLOW.frames)} ms a frame: still hero`);
				still = true;
				pause();
				return;
			}
		}
		const dt = Math.min(0.1, (now - last) / 1000);
		last = now;
		introTime += dt;
		const gridIntro = Math.min(1, introTime / INTRO.grid);
		const handIntro = Math.min(1, Math.max(0, (introTime - INTRO.grid - INTRO.gap) / INTRO.hand));
		wave?.draw((now - start) / 1000, gridIntro, dt);
		if (field) {
			field.intro = handIntro;
		}
		if (!introDone) {
			// outline and fill come in with the particles
			showMark(handIntro >= 1 ? 1 : easeOut((handIntro - 0.2) / 0.8, 2));
			if (handIntro >= 1) {
				finishIntro();
			}
		}
		const mouseStill = next.valid === mouse.valid && next.x === mouse.x && next.y === mouse.y;
		if (mark && field && !(resting && mouseStill)) {
			// a re-entry never sweeps a long path from the old position
			mouse.px = mouse.valid && next.valid ? mouse.x : next.x;
			mouse.py = mouse.valid && next.valid ? mouse.y : next.y;
			mouse.x = next.x;
			mouse.y = next.y;
			mouse.valid = next.valid;
			const most = step(field, mouse, dt);
			resting = introDone && mouseStill && most < 0.01;
			// clip to the shape again over the last 30% of the fly-in
			mark.draw(handIntro >= 1 ? 1 : easeOut((handIntro - 0.7) / 0.3, 2));
		}
		raf = requestAnimationFrame(tick);
	};
	const play = (): void => {
		if (raf || still || !visible || document.hidden) {
			return;
		}
		last = performance.now();
		raf = requestAnimationFrame(tick);
	};
	const pause = (): void => {
		cancelAnimationFrame(raf);
		raf = 0;
	};

	new IntersectionObserver(([entry]) => {
		visible = entry.isIntersecting;
		if (visible) {
			play();
		} else {
			pause();
		}
	}).observe(hero);
	document.addEventListener('visibilitychange', () => (document.hidden ? pause() : play()));
	play();
}

// start after first paint so the h1 (LCP) is never held up by shader compiles
const run = (): void => {
	const go = (): void => {
		const heroes = document.querySelectorAll<HTMLElement>('[data-px-block="hero"]');
		if (heroes.length) {
			(isStill() ? Promise.resolve(false) : softwareWebGL()).then((software) => heroes.forEach((hero) => init(hero, software)));
		}
	};
	if ('requestIdleCallback' in window) {
		requestIdleCallback(go, { timeout: 500 });
	} else {
		setTimeout(go, 1);
	}
};
if (document.readyState === 'loading') {
	document.addEventListener('DOMContentLoaded', run);
} else {
	run();
}
